import { Component } from '@theme/component';

/**
 * Randomizes and controls the homepage testimonials carousel.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [track]
 * @property {HTMLButtonElement} [previousButton]
 * @property {HTMLButtonElement} [nextButton]
 *
 * @extends Component<Refs>
 */
class HomeTestimonialsCarousel extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#prepareSlides();
    this.#resetToStart();

    this.refs.track?.addEventListener('scroll', this.#handleScroll, { passive: true });
    window.addEventListener('resize', this.#handleResize);

    window.requestAnimationFrame(() => this.#resetToStart());
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.track?.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('resize', this.#handleResize);
  }

  previous() {
    this.#scrollBySlide(-1);
  }

  next() {
    this.#scrollBySlide(1);
  }

  #prepareSlides() {
    const { track } = this.refs;

    if (!(track instanceof HTMLElement) || track.dataset.prepared === 'true') return;

    const slides = Array.from(track.querySelectorAll('[data-home-testimonials-slide]'));
    const visibleSlides = this.#shuffle(slides).slice(0, this.limit);

    track.replaceChildren(...visibleSlides);
    track.dataset.prepared = 'true';
    this.setAttribute('initialized', '');
  }

  #resetToStart() {
    const { track } = this.refs;

    if (track instanceof HTMLElement) {
      track.scrollLeft = 0;
    }

    this.#updateControls();
  }

  /**
   * @param {number} direction
   */
  #scrollBySlide(direction) {
    const { track } = this.refs;

    if (!(track instanceof HTMLElement)) return;

    const firstSlide = track.querySelector('[data-home-testimonials-slide]');

    if (!(firstSlide instanceof HTMLElement)) return;

    const trackStyles = window.getComputedStyle(track);
    const gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap || '0') || 0;
    const slideStep = firstSlide.getBoundingClientRect().width + gap;

    track.scrollBy({
      left: slideStep * direction,
      behavior: 'smooth',
    });
  }

  #updateControls() {
    const { track, previousButton, nextButton } = this.refs;

    if (
      !(track instanceof HTMLElement) ||
      !(previousButton instanceof HTMLButtonElement) ||
      !(nextButton instanceof HTMLButtonElement)
    ) {
      return;
    }

    const maxScroll = track.scrollWidth - track.clientWidth;
    const scrollLeft = track.scrollLeft;
    const canScroll = maxScroll > 1;

    previousButton.disabled = !canScroll || scrollLeft <= 1;
    nextButton.disabled = !canScroll || scrollLeft >= maxScroll - 1;
  }

  /**
   * @template T
   * @param {T[]} items
   * @returns {T[]}
   */
  #shuffle(items) {
    const shuffledItems = [...items];

    for (let index = shuffledItems.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [shuffledItems[index], shuffledItems[randomIndex]] = [shuffledItems[randomIndex], shuffledItems[index]];
    }

    return shuffledItems;
  }

  get limit() {
    const limit = Number(this.dataset.limit);

    return Number.isFinite(limit) && limit > 0 ? limit : 12;
  }

  #handleScroll = () => {
    window.requestAnimationFrame(() => this.#updateControls());
  };

  #handleResize = () => {
    this.#updateControls();
  };
}

if (!customElements.get('home-testimonials-carousel')) {
  customElements.define('home-testimonials-carousel', HomeTestimonialsCarousel);
}
